import dayjs from 'dayjs'
import { ApiRoutes } from '../helpers/routes'
import fetchApi from './fetchApi'
import { formatDateStringToLocalDate } from '../helpers/utils'

const baseUrl = `${process.env.REACT_APP_API_URL}/api/statistics`

const getStatisticsFilters = (career?: string, startYear?: string, endYear?: string) => {
    return new URLSearchParams({
        ...(!!career ? { career: career } : {}),
        ...(!!startYear
            ? { startYear: dayjs(formatDateStringToLocalDate(startYear)).format('YYYY') }
            : {}),
        ...(!!endYear
            ? { endYear: dayjs(formatDateStringToLocalDate(endYear)).format('YYYY') }
            : {}),
    }).toString()
}

class StatisticsService {
    getHierarchicalPositions = async (carreer?: string, startDate?: string, endDate?: string) => {
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.hierarchicalPositions}?${getStatisticsFilters(carreer, startDate, endDate)}`,
            {
                method: 'GET',
            },
        )
    }

    getDaysToReachHierarchicalPosition = async (
        position: string,
        carreer?: string,
        startDate?: string,
        endDate?: string,
    ) => {
        const positionParam = new URLSearchParams({ hierarchicalPosition: position }).toString()
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.daysToReachHierarchicalPosition}?${getStatisticsFilters(carreer, startDate, endDate)}&${positionParam}`,
            {
                method: 'GET',
            },
        )
    }

    getCommonRoles = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.commonRoles}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getCommonAreas = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.commonAreas}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getWorkPlacePercentage = async (carreer?: string, startDate?: string, endDate?: string) => {
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.workPlace}?${getStatisticsFilters(carreer, startDate, endDate)}`,
            {
                method: 'GET',
            },
        )
    }

    getFreelancerOrEntrepreneurGraduates = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
    ) => {
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.freelancerOrEntrepreneurGraduates}?${getStatisticsFilters(carreer, startDate, endDate)}`,
            {
                method: 'GET',
            },
        )
    }

    getCommonAbilities = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.commonAbilities}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getUnemployedAbilities = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.unemployedAbilities}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getCommonWorkAbilities = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.commonWorkAbilities}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getAverageTimeInCompany = async (carreer?: string, startDate?: string, endDate?: string) => {
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.averageTimeInCompany}?${getStatisticsFilters(carreer, startDate, endDate)}`,
            {
                method: 'GET',
            },
        )
    }

    getCommonCompanies = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.commonCompanies}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getCommonAreasByHierarchy = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.commonAreasByHierarchy}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getUsersWithoutWork = async (carreer?: string, startDate?: string, endDate?: string) => {
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.usersWithoutWork}?${getStatisticsFilters(carreer, startDate, endDate)}`,
            {
                method: 'GET',
            },
        )
    }

    getDaysToGraduate = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        years?: string,
    ) => {
        const amountParam = new URLSearchParams({ years: years ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.daysToGraduate}?${getStatisticsFilters(carreer, startDate, endDate)}${years && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    searchGraduatesWithAptitudes = async (
        abilities: string[],
        carreer?: string,
        startDate?: string,
        endDate?: string,
    ) => {
        let params = ''
        abilities.forEach((ability) => {
            const param = new URLSearchParams({ aptitudes: ability }).toString()
            params += `&${param}`
        })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.graduatesWithAptitudes}?${getStatisticsFilters(carreer, startDate, endDate)}${params}`,
            {
                method: 'GET',
            },
        )
    }

    searchGraduatesInCompanies = async (
        companies: string[],
        carreer?: string,
        startDate?: string,
        endDate?: string,
    ) => {
        let params = ''
        companies.forEach((company) => {
            const param = new URLSearchParams({ companies: company }).toString()
            params += `&${param}`
        })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.graduatesInCompanies}?${getStatisticsFilters(carreer, startDate, endDate)}${params}`,
            {
                method: 'GET',
            },
        )
    }

    searchGraduatesInJobTitles = async (
        jobTitles: string[],
        carreer?: string,
        startDate?: string,
        endDate?: string,
    ) => {
        let params = ''
        jobTitles.forEach((jobTitle) => {
            const param = new URLSearchParams({ jobTitles: jobTitle }).toString()
            params += `&${param}`
        })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.graduatesInJobTitles}?${getStatisticsFilters(carreer, startDate, endDate)}${params}`,
            {
                method: 'GET',
            },
        )
    }

    getAverageToGetFirstJob = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        years?: string,
    ) => {
        const amountParam = new URLSearchParams({ years: years ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.averageToGetFirstJob}?${getStatisticsFilters(carreer, startDate, endDate)}${years && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getGraduatesWithPostGraduateDegrees = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
    ) => {
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.graduatesWithPostGraduateDegrees}?${getStatisticsFilters(carreer, startDate, endDate)}`,
            {
                method: 'GET',
            },
        )
    }

    getMostCommonFirstJobsByCompany = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.topFirstJobCommonCompanies}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }

    getMostCommonFirstJobsByJob = async (
        carreer?: string,
        startDate?: string,
        endDate?: string,
        roleNumbers?: string,
    ) => {
        const amountParam = new URLSearchParams({ amount: roleNumbers ?? '' })
        return fetchApi(
            `${baseUrl}${ApiRoutes.statistics.topFirstJobCommonJobTitles}?${getStatisticsFilters(carreer, startDate, endDate)}${roleNumbers && '&' + amountParam}`,
            {
                method: 'GET',
            },
        )
    }
}

export default StatisticsService
